import { isPlatformBrowser } from '@angular/common';
import {
  Component,
  EventEmitter,
  HostBinding,
  Inject,
  OnInit,
  Output,
  PLATFORM_ID,
  computed,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationStart, Router, Event as RouterEvent } from '@angular/router';
import { combineLatest, fromEvent } from 'rxjs';
import { environment } from '../../../environments/environment';
import { BrandedIcons } from '../../models/icon/branded-icon';
import { User } from '../../models/user/user';
import { AuthService } from '../../services/auth/auth.service';
import { ContentService } from '../../services/content/content.service';
import { IconService } from '../../services/icon/icon.service';
import { PlayerService } from '../../services/player/player.service';
import { UtilsService } from '../../services/utils/utils.service';
import { ContentMenu } from '../../models/content/content';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: '[appHeader]',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: false,
})
export class HeaderComponent implements OnInit {
  @HostBinding('class.app-header--scrolledDown') scrolledDown = false;
  @HostBinding('class.app-header--animatedLogoVIBE') animatedLogoVIBE = false;

  @Output() menuOpened: EventEmitter<any> = new EventEmitter();

  siteTitle: string;
  currentUser = toSignal(this.authService.currentUser, {
    initialValue: null as User | null,
  });
  menu: ContentMenu;
  menuPlatformPosition: number;
  showPlayerButton: boolean;
  pageIsStandalone: boolean;
  forceHideSubmenu: boolean;
  brandedIcons: BrandedIcons;
  numberOfAcceptedOrganisationCallsInMenu =
    environment.numberOfAcceptedOrganisationCallsInMenu;
  currentUserPendingInvitesCount = computed(() => {
    const user = this.currentUser();
    return user
      ? user.invites?.items.filter(
          (userInvite) => userInvite.currentState.state === 'PENDING',
        )?.length || 0
      : 0;
  });
  adminLabel = computed(() => {
    const user = this.currentUser();
    if (user?.roles?.includes('ADMIN')) {
      return $localize`Admin`;
    } else if (user?.roles?.includes('REGION_ADMIN')) {
      return $localize`Regio Admin`;
    }
    return $localize`Admin`;
  });

  constructor(
    @Inject(PLATFORM_ID) private platformId: string,
    private authService: AuthService,
    private contentService: ContentService,
    private playerService: PlayerService,
    private utilsService: UtilsService,
    private iconService: IconService,
    private router: Router,
  ) {}

  ngOnInit() {
    this.brandedIcons = this.iconService.getBrandedIcons();

    // Get the site title
    this.siteTitle = this.contentService.getContentByPath('settings.siteTitle');

    // Get the simple menu
    this.menu = this.contentService.getContentByPath('menus.simple');
    this.menuPlatformPosition = this.contentService.getContentByPath(
      'menus.simplePlatformPosition',
    );

    // Subscribe to whether the current page is standalone
    this.utilsService.pageIsStandalone.subscribe(
      (pageIsStandalone) => (this.pageIsStandalone = pageIsStandalone),
    );

    // Watch the player service and determine whether the player button should be shown
    combineLatest([
      this.playerService.open,
      this.playerService.current,
      this.playerService.queue,
      this.playerService.playerLoading,
    ]).subscribe((result) => {
      const isOpen = result[0];
      const hasCurrent = !!result[1];
      const hasQueue = result[2]?.length > 0;
      const isPlayerLoading = result[3];
      this.showPlayerButton =
        !isPlayerLoading && !isOpen && (hasCurrent || hasQueue);
    });

    // If the client is the browser & the license is VI.BE
    if (isPlatformBrowser(this.platformId) && environment.license === 'vibe') {
      // Subscribe to window scroll
      fromEvent(window, 'scroll').subscribe(
        (_event) =>
          (this.scrolledDown =
            (window.pageYOffset || document.documentElement.scrollTop) > 0),
      );

      // Subscribe to animated logo shown
      this.utilsService.headerAnimatedLogo.subscribe(
        (headerAnimatedLogo) => (this.animatedLogoVIBE = headerAnimatedLogo),
      );
    }

    // Hide the open submenu when a navigation starts
    this.router.events.subscribe((event: RouterEvent) => {
      if (event instanceof NavigationStart) {
        this.forceHideSubmenu = true;
        setTimeout(() => {
          this.forceHideSubmenu = false;
        }, 50);
      }
    });
  }

  openPlayer() {
    this.playerService.openRequested.next();
  }

  signout() {
    this.router.navigateByUrl('/');
    this.authService.signout();
  }
}
